// D3-06 (FE-1.8): map raw MSAL / Azure AD error strings to friendly copy.
//
// MSAL surfaces errors like "interaction_required" or
// "AADSTS50076: Due to a configuration change ..." — cryptic OAuth plumbing
// that should never reach end users. The raw error is still logged to the
// console by AuthContext for diagnostics.

interface AuthErrorRule {
  /** Case-insensitive substrings that identify this error family. */
  match: string[];
  message: string;
}

const AUTH_ERROR_RULES: AuthErrorRule[] = [
  {
    // Session/token expired or interaction required → sign in again.
    match: [
      'interaction_required',
      'login_required',
      'consent_required',
      'invalid_grant',
      'aadsts50076',
      'aadsts50079',
      'aadsts50058',
      'aadsts70008',
      'aadsts700082',
      'aadsts50173',
      'token_expired',
      'refresh_token',
    ],
    message: 'Your session expired — please sign in again.',
  },
  {
    // User closed the popup / cancelled the flow.
    match: ['user_cancelled', 'user_canceled', 'aadsts65004', 'popup_window_closed'],
    message: 'Sign-in was cancelled. Try again when you\u2019re ready.',
  },
  {
    // Popup blocked or hung.
    match: ['popup_window_error', 'monitor_window_timeout', 'popup blocked', 'popups are blocked'],
    message:
      'We couldn\u2019t open the Microsoft sign-in window. Allow popups for this site and try again.',
  },
  {
    // Account exists but isn't allowed into this app/tenant.
    match: ['aadsts50105', 'aadsts50020', 'aadsts700016', 'unauthorized_client', 'access_denied'],
    message:
      'This account doesn\u2019t have access to Ally. Contact IT if you think this is a mistake.',
  },
  {
    // Network problems reaching Microsoft or our API.
    match: ['failed to fetch', 'network', 'timeout', 'econnrefused', 'load failed'],
    message: 'We couldn\u2019t reach the sign-in service. Check your connection and try again.',
  },
];

const DEFAULT_AUTH_ERROR_MESSAGE =
  'We couldn\u2019t sign you in. Please try again — contact IT if this keeps happening.';

export function friendlyAuthError(rawError: string | null | undefined): string {
  const raw = String(rawError ?? '').trim();
  if (!raw) {
    return DEFAULT_AUTH_ERROR_MESSAGE;
  }

  const haystack = raw.toLowerCase();
  for (const rule of AUTH_ERROR_RULES) {
    if (rule.match.some((needle) => haystack.includes(needle))) {
      return rule.message;
    }
  }

  return DEFAULT_AUTH_ERROR_MESSAGE;
}

export default friendlyAuthError;
